import { DomainError } from './errors'

/** RES-2. The order matters only for display. */
export const RESERVATION_STATUSES = [
  'draft',
  'confirmed',
  'checked_out',
  'returned',
  'closed',
  'cancelled',
  'no_show',
] as const

export type ReservationStatus = (typeof RESERVATION_STATUSES)[number]

/**
 * ARCHITECTURE: draft → confirmed → checked_out → returned → closed, or
 * cancelled / no_show. A no-show needs a confirmed booking; once the vehicle
 * has left, only the return path remains. Terminal states have no exits.
 */
const NEXT: Readonly<Record<ReservationStatus, readonly ReservationStatus[]>> = {
  draft: ['confirmed', 'cancelled'],
  confirmed: ['checked_out', 'cancelled', 'no_show'],
  checked_out: ['returned'],
  returned: ['closed'],
  closed: [],
  cancelled: [],
  no_show: [],
}

export function allowedTransitions(from: ReservationStatus): readonly ReservationStatus[] {
  return NEXT[from]
}

export function canTransition(from: ReservationStatus, to: ReservationStatus): boolean {
  return NEXT[from].includes(to)
}

export function assertTransition(from: ReservationStatus, to: ReservationStatus): void {
  if (!canTransition(from, to)) {
    throw new DomainError('illegal_transition', `a ${from} reservation cannot become ${to}`, {
      from,
      to,
      allowed: NEXT[from],
    })
  }
}

/**
 * Whether a reservation still claims a vehicle at `now`. A draft claims one
 * only while its hold is live; open question Q-13 decides how long that is.
 */
export function holdsCapacity(
  status: ReservationStatus,
  holdExpiresAt: Date | null,
  now: Date,
): boolean {
  switch (status) {
    case 'confirmed':
    case 'checked_out':
      return true
    case 'draft':
      return holdExpiresAt !== null && holdExpiresAt.getTime() > now.getTime()
    default:
      return false
  }
}

/** RES-5: a booking that ended without a rental; it owes its cancellation fee, not its total. */
/**
 * The endings: the states with no exit. A booking in one is over, whatever is
 * still owed on it (S8 waits for the account too, `packages/domain/src/privacy.ts`).
 */
export const ENDED_STATUSES: readonly ReservationStatus[] = ['closed', 'cancelled', 'no_show']

export const ENDED_WITHOUT_RENTAL: readonly ReservationStatus[] = ['cancelled', 'no_show']

/**
 * PAY-1: the bookings the counter takes money for. A cancelled or no-show one
 * may still owe its fee, paid at the counter (S3); never more than is owed.
 * S5 (Fady, 15 Oct): a closed one too, once it owes again (a dispute lost, a
 * refund made in Stripe's Dashboard beyond what was owed). Online, never.
 */
export const TAKES_COUNTER_PAYMENT: readonly ReservationStatus[] = [
  'confirmed',
  'checked_out',
  'returned',
  'cancelled',
  'no_show',
  'closed',
]

/** WEB-2 and RES-4: the bookings Stripe's page and a payment link take money for. */
export const TAKES_ONLINE_PAYMENT: readonly ReservationStatus[] = [
  'confirmed',
  'checked_out',
  'returned',
]

/**
 * Whether Stripe's page and a payment link take money for a booking now
 * (whether anything is owed is the caller's check). S6 (decision 4): a closed
 * booking does while what it owes is its fines' admin fees, collected by link
 * since the renter has left; anything more is settled at the counter, as S5
 * decided for money owed again (a refund made in Stripe's Dashboard beyond
 * what was owed). The account does not say which payment paid which charge,
 * so "what it owes is its fees" is a balance no larger than the fees charged.
 */
export function takesOnlinePayment(
  status: ReservationStatus,
  owes: { balanceCents: number; fineFeesCents: number },
): boolean {
  if (TAKES_ONLINE_PAYMENT.includes(status)) return true
  return status === 'closed' && owesOnlyFineFees(owes)
}

/** Something is owed, and no more than the fines' admin fees charged (S6). */
export function owesOnlyFineFees(owes: { balanceCents: number; fineFeesCents: number }): boolean {
  return owes.fineFeesCents > 0 && owes.balanceCents > 0 && owes.balanceCents <= owes.fineFeesCents
}

/**
 * CUS-2: the bookings that still take identity documents, from the website or
 * the counter. One that ended or was closed collects no more personal data.
 */
export const TAKES_DOCUMENTS: readonly ReservationStatus[] = [
  'confirmed',
  'checked_out',
  'returned',
]
